// Plyx standalone preview mode.
//
// Plyx is its own product, served from the same React build as AfriMarket for
// now. We auto-engage standalone mode in four independent ways so the user
// experience matches "Plyx is a separate app":
//
//   1. Default-app flag — REACT_APP_DEFAULT_APP=plyx makes Plyx the default
//      for THIS deployment (preview env or plyx.co.uk). Bare `/` shows Plyx.
//   2. Hostname  — any host beginning with "plyx." (e.g. plyx.co.uk,
//      plyx.preview.emergentagent.com) auto-engages and stays engaged.
//   3. Path      — any URL beginning with "/plyx" auto-engages and persists
//      the flag, so visitors can share `…/plyx` as a clean shareable link.
//   4. Query     — `?standalone=plyx` / `?app=plyx` toggles it on. `=off` /
//      `=exit` / `=afrimarket` clears it. Useful for explicit linking.
//
// In standalone mode:
//   - All AfriMarket routes (/, /browse, /listing/:id, /dashboard, …) redirect to /plyx
//   - Cross-promo "Back to AfriMarket" links are hidden
//   - The product behaves as if Plyx is the entire app
//
// Exit: click "EXIT" in the Plyx preview chip, visit /?app=afrimarket, or
// call exitPlyxStandalone().
const STORAGE_KEY = "plyx_standalone_mode";
const DEFAULT_APP = (process.env.REACT_APP_DEFAULT_APP || "afrimarket").toLowerCase();

const _hostnameLooksLikePlyx = () => {
  try {
    const h = (window.location.hostname || "").toLowerCase();
    // Match: plyx.co.uk, www.plyx.co.uk, preview.plyx.co.uk, plyx.app,
    // plyx alone, plyx.foo.bar, etc. Anything where "plyx" is a label
    // somewhere in the hostname (not just a prefix like "plyx-foo.com").
    return (
      h === "plyx"
      || /(^|\.)plyx(\.|$)/i.test(h)
    );
  } catch {
    return false;
  }
};

const _hostnameLooksLikeAfriMarket = () => {
  try {
    const h = (window.location.hostname || "").toLowerCase();
    // Match: afrimarket.org, www.afrimarket.org, any *.afrimarket.* host.
    // This domain ALWAYS shows AfriMarket regardless of any sticky flags
    // or build-time defaults that may have leaked in from Plyx deploys.
    return /(^|\.)afrimarket(\.|$)/i.test(h);
  } catch {
    return false;
  }
};

/** True when the page is being served from the real Plyx production domain
 *  (plyx.co.uk, www.plyx.co.uk, plyx.foo, …) — used to hide the "Preview ·
 *  Exit" chip on the live site so visitors don't see a preview tag. */
export const isPlyxProductionDomain = () => _hostnameLooksLikePlyx();

const _pathLooksLikePlyx = () => {
  try {
    const p = (window.location.pathname || "").toLowerCase();
    return p === "/plyx" || p.startsWith("/plyx/");
  } catch {
    return false;
  }
};

const _pathLooksLikeAfriMarket = () => {
  try {
    const p = (window.location.pathname || "").toLowerCase();
    // AfriMarket-only paths that should always render AfriMarket even when
    // REACT_APP_DEFAULT_APP=plyx (preview env). Add new AfriMarket-only
    // sections here as they ship.
    if (p === "/afrimarket" || p.startsWith("/afrimarket/")) return true;
    if (p === "/makers" || p.startsWith("/makers/")) return true;
    if (p === "/movies" || p.startsWith("/movies/")) return true;
    if (p === "/shops" || p === "/shop" || p.startsWith("/shop/") || p === "/dashboard/shop") return true;
    // Authenticated AfriMarket seller/buyer areas (so deep links + post-login
    // redirects resolve to AfriMarket, not Plyx, in the preview env).
    if (p === "/dashboard" || p.startsWith("/dashboard/")) return true;
    if (p === "/purchases" || p === "/sales") return true;
    if (p === "/fulfil" || p === "/admin" || p.startsWith("/admin/")) return true;
    if (p.startsWith("/listing/") || p.startsWith("/order/")) return true;
    return false;
  } catch {
    return false;
  }
};

export const isPlyxStandaloneMode = () => {
  try {
    // 0. AfriMarket hostname guard — if we're on afrimarket.org (or any
    //    *.afrimarket.* host), ALWAYS show AfriMarket. Clear any sticky
    //    Plyx flag that may have leaked in from a previous /plyx visit
    //    or a Plyx-default deploy. This must run before every other
    //    check so the main domain can never be hijacked by Plyx.
    if (_hostnameLooksLikeAfriMarket()) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      // Honour an explicit ?app=plyx override only when the user asks
      // for it directly via the query string (so internal links still
      // work). Anything else → AfriMarket.
      const sp0 = new URLSearchParams(window.location.search);
      const flag0 = sp0.get("standalone") || sp0.get("app");
      if (flag0 === "plyx") return true;
      return false;
    }
    // 1. Query string takes precedence and persists for future sessions
    const sp = new URLSearchParams(window.location.search);
    const flag = sp.get("standalone") || sp.get("app");
    if (flag === "off" || flag === "exit" || flag === "afrimarket") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    if (flag === "plyx") {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    // 2. Explicit /afrimarket* path → AfriMarket, even with default-app=plyx
    if (_pathLooksLikeAfriMarket()) {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    // 3. Hostname — `plyx.*` always means standalone
    if (_hostnameLooksLikePlyx()) {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    // 4. Path — visiting any /plyx* URL flips the user into standalone and
    //    persists it, so subsequent navigations stay in Plyx.
    if (_pathLooksLikePlyx()) {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    // 5. Sticky fallback — flag already set in a previous session
    if (localStorage.getItem(STORAGE_KEY) === "1") return true;
    // 6. Build-time default — REACT_APP_DEFAULT_APP=plyx makes the bare
    //    root URL show Plyx. AfriMarket reachable via /?app=afrimarket
    //    or /afrimarket.
    return DEFAULT_APP === "plyx";
  } catch {
    return false;
  }
};

export const exitPlyxStandalone = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  // If the default-app is Plyx, we need a stronger signal to land on
  // AfriMarket — explicitly navigate to /?app=afrimarket.
  try {
    if (DEFAULT_APP === "plyx") {
      window.location.href = "/?app=afrimarket";
    }
  } catch { /* ignore */ }
};
